import { c as _c } from "react/compiler-runtime";
import { Node, mergeAttributes } from "@tiptap/core";
import { NodeViewProps, NodeViewWrapper, ReactNodeViewRenderer } from "@tiptap/react";
import { ReferenceType, getRouteForReference, serializeReference } from "@/lib/notification-reference";
import { cx } from "@/lib/tailwind-utils";
import { UserType } from "@/types/enums/user-type";
import { patientSidebarDefinition } from "@/features/sidebar/patient-sidebar";
import { useRightSidebarStore } from "@/stores/right-sidebar-store";
import { useTenantStore } from "@/stores/tenant-store";
const CHIP_CLASS = "rounded bg-primary/10 box-decoration-clone px-[0.3rem] py-[0.1rem] text-primary";

/** Split a reference display name into first/last (handles "Last, First" and "First Last"). */
function parsePatientName(display: string): {
  firstName: string;
  lastName: string;
} {
  const trimmed = display.trim();
  if (trimmed.includes(",")) {
    const [last, ...rest] = trimmed.split(",").map(s => s.trim());
    return {
      firstName: rest.join(" ") || "",
      lastName: last
    };
  }
  const parts = trimmed.split(" ");
  return {
    firstName: parts[0] || "",
    lastName: parts.slice(1).join(" ") || ""
  };
}

/**
 * Renders an embedded reference (`{{type:display:id}}`) as an atomic chip inside
 * the editor. The chip is clickable: patient refs open the patient sidebar. This
 * deliberately does NOT navigate away when read from a patient chart the way the
 * read-only renderers do — this chip lives inside an editable description or
 * comment, and leaving the page would discard whatever is being typed. Other
 * routable refs (charge, payment, invoice, …) open the linked page in a new tab —
 * mirroring NotificationReferenceContent. Non-routable refs stay display-only.
 *
 * Navigation happens on click; the editor's mousedown selection is suppressed so a
 * click navigates instead of just selecting the atom. Opening in a new tab keeps an
 * in-progress description edit from being thrown away.
 */
export function ReferenceMentionView(props) {
  const $ = _c(19);
  const openSidebar = useRightSidebarStore(_temp);
  const {
    getCurrentTenant
  } = useTenantStore();
  const refType = props.node.attrs.refType as ReferenceType | null;
  const id = props.node.attrs.id as string | null;
  const display = props.node.attrs.display as string ?? "";
  const isPatient = refType === "patient" && !!id;
  let t0;
  if ($[0] !== id || $[1] !== refType) {
    t0 = refType && id ? getRouteForReference(refType, id) : null;
    $[0] = id;
    $[1] = refType;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const route = t0;
  const clickable = isPatient || !!route;
  let t1;
  if ($[3] !== display || $[4] !== getCurrentTenant || $[5] !== id || $[6] !== isPatient || $[7] !== openSidebar || $[8] !== route) {
    t1 = () => {
      const tenant = getCurrentTenant();
      if (isPatient && id) {
        const {
          firstName,
          lastName
        } = parsePatientName(display);
        openSidebar(patientSidebarDefinition, {
          patientTiny: {
            id,
            firstName,
            lastName,
            type: UserType.PATIENT
          }
        });
        return;
      }
      if (route) {
        const url = tenant ? `/${tenant}${route}` : route;
        window.open(url, "_blank", "noopener,noreferrer");
      }
    };
    $[3] = display;
    $[4] = getCurrentTenant;
    $[5] = id;
    $[6] = isPatient;
    $[7] = openSidebar;
    $[8] = route;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  const activate = t1;
  const t2 = clickable && "cursor-pointer hover:bg-primary/20";
  let t3;
  if ($[10] !== t2) {
    t3 = cx(CHIP_CLASS, t2);
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== activate || $[13] !== clickable) {
    t4 = clickable ? {
      contentEditable: false,
      role: "link",
      tabIndex: 0,
      onMouseDown: _temp2,
      onClick: e_0 => {
        e_0.preventDefault();
        e_0.stopPropagation();
        activate();
      },
      onKeyDown: e_1 => {
        if (e_1.key === "Enter" || e_1.key === " ") {
          e_1.preventDefault();
          activate();
        }
      }
    } : {};
    $[12] = activate;
    $[13] = clickable;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== display || $[16] !== t3 || $[17] !== t4) {
    t5 = <NodeViewWrapper as="span" className="inline w-fit"><span data-testid="reference-mention-chip" className={t3} {...t4}>{display}</span></NodeViewWrapper>;
    $[15] = display;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  return t5;
}

/**
 * Inline atom node representing an embedded notification reference.
 *
 * Reference tokens arrive in the content as `{{type:display:id}}`. The editor
 * loads them as `<reference-mention data-type data-display data-id>` elements
 * (see `transformContentToMentions`), renders them as chips, and serializes
 * them back to the token format via `renderText` so `editor.getText()` (used on
 * save) preserves the reference instead of dropping it to raw text.
 */
function _temp2(e) {
  return e.preventDefault();
}
function _temp(state) {
  return state.open;
}
export const ReferenceMention = Node.create({
  name: "referenceMention",
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,
  draggable: false,
  addAttributes() {
    return {
      refType: {
        default: null,
        parseHTML: element => element.getAttribute("data-type"),
        renderHTML: attributes => attributes.refType ? {
          "data-type": attributes.refType
        } : {}
      },
      display: {
        default: "",
        parseHTML: element => element.getAttribute("data-display") ?? "",
        renderHTML: attributes => ({
          "data-display": attributes.display
        })
      },
      id: {
        default: null,
        parseHTML: element => element.getAttribute("data-id"),
        renderHTML: attributes => attributes.id ? {
          "data-id": attributes.id
        } : {}
      }
    };
  },
  parseHTML() {
    return [{
      tag: "reference-mention"
    }];
  },
  renderHTML({
    HTMLAttributes
  }) {
    return ["reference-mention", mergeAttributes(HTMLAttributes)];
  },
  renderText({
    node
  }) {
    return serializeReference({
      type: node.attrs.refType as ReferenceType,
      display: node.attrs.display ?? "",
      id: node.attrs.id ?? ""
    });
  },
  addNodeView() {
    return ReactNodeViewRenderer(ReferenceMentionView);
  }
});